<template>
  <div>
    <h4>Critères de correction</h4>
    <div class="my-3">
      <div>{{ commentToEdit.pub.user.nickname }}</div>
      <small class="text-muted"
        >{{ commentToEdit.pub.created_at | formatDateTime }}
      </small>
    </div>
    <b-list-group-item class="my-3"
      >{{ commentToEdit.pub.comment }}
    </b-list-group-item>
    <hr />

    <transition name="slide" mode="out-in">
      <UnitLoading key="loading" v-if="gridStatus == 'fetching'" />
      <div v-else key="content">
        <b-overlay :show="loading" :opacity="0.3" variant="light" blur="1rem">
          <template v-slot:overlay>
            <b-spinner class="text-secondary"></b-spinner>
          </template>
          <template v-if="grid">
            <b-form-group>
              <b-form-radio
                v-for="criteria in grid.comment_criteria"
                :key="criteria.id"
                v-model="criteriaSelected"
                :value="criteria"
                button
                :button-variant="
                  getVariant(
                    criteriaVariant[criteria.id],
                    criteria,
                    criteriaSelected
                  )
                "
                name="criterias"
                @click.native="onCommentGrade(criteria, criteriaSelected)"
                >{{ criteria.shortcut }}</b-form-radio
              >
            </b-form-group>
            <hr />
            <p>Source</p>
            <b-form-group>
              <b-form-radio
                v-for="source in grid.reference_criteria"
                :key="source.id"
                v-model="sourceSelected"
                :value="source"
                button
                :button-variant="
                  getVariant(sourceVariant[source.id], source, sourceSelected)
                "
                name="sources"
                @click.native="onRefGrade(source, sourceSelected)"
                >{{ source.shortcut }}</b-form-radio
              >
            </b-form-group>
          </template>

          <b-badge
            pill
            variant="info"
            class="px-2 mr-2"
            v-if="criteriaSelected.comment"
          >
            {{ criteriaSelected.comment }}
          </b-badge>
          <b-badge
            pill
            variant="info"
            class="px-2 mr-2"
            v-if="sourceSelected.comment"
          >
            {{ sourceSelected.comment }}
          </b-badge>
        </b-overlay>
        <section>
          <b-link
            target="_blank"
            :to="{ name: 'teachers.evaluations', params: { type: 'grille' } }"
          >
            <b-icon icon="gear-fill" variant="secondary"></b-icon>
            <small> Modifier vos critères de correction</small>
          </b-link>
        </section>
      </div>
    </transition>
  </div>
</template>

<script>
/**
 * @file       Comment grade component.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */

import Vue from "vue";
import { mapState } from "vuex";
import { debounce } from "lodash";
import { commentsMixin } from "@/modules/comments/mixins";
import UnitLoading from "@/modules/comments/components/unitLoading.vue";

/**
 * Export the component.
 * 
 * @type  {Object}
 */
export default {
  name: "gradComment",
  components: { UnitLoading },
  mixins: [commentsMixin],

  /**
   * Data used in the component.
   *
   * @return  {Object}
   */
  data() {
    return {
      criteriaSelected: {},
      sourceSelected: {},
      criteriaVariant: [],
      sourceVariant: [],
      loading: false
    };
  },
  mounted() {
    this.$store.dispatch("grid/getGrid", {
      group: this.dlibr.group
    });
    if (this.commentToEdit.pub.comment_criteria) {
      this.criteriaSelected = this.commentToEdit.pub.comment_criteria;
    }
    if (this.commentToEdit.pub.reference_criteria) {
      this.sourceSelected = this.commentToEdit.pub.reference_criteria;
    }
  },
  destroyed() {
    this.$store.commit("grid/getGrid");
  },

  /**
   * Computed properties.
   * 
   * @type  {Object}
   */
  computed: {
    ...mapState({
      grid: state =>
        state.grid.comment.data.data
          ? state.grid.comment.data.data.find(g => g)
          : {},
      gridStatus: state => state.grid.comment.status
    })
  },

  /**
   * Methods.
   * 
   * @type  {Object}
   */
  methods: {
    /**
     * Called on comment grade.
     * 
     * @param  {Object}   value       The value
     * @param  {Object}   selected    The selected item
     */
    onCommentGrade: debounce(function(value, selected) {
      this.loading = true;
      let status =
        value.id == selected.id ? false : !this.criteriaVariant[value.id];
      this.criteriaVariant = [];
      const payload = {};

      payload.comment = this.commentToEdit.pub.id;
      if (status) {
        payload.comment_criteria = value.id;
      } else {
        this.criteriaSelected = {};
      }
      Vue.set(this.criteriaVariant, value.id, status);
      this.$store.dispatch("comments/postCommentGrade", payload).then(() => {
        this.loading = false;
        this.updateStetaComment();
      });
    }),

    /**
     * Called on reference grade.
     * 
     * @param  {Object}   value       The value
     * @param  {Object}   selected    The selected item
     */
    onRefGrade: debounce(function(value, selected) {
      this.loading = true;
      let status =
        value.id == selected.id ? false : !this.sourceVariant[value.id];
      this.sourceVariant = [];
      const payload = {};

      payload.comment = this.commentToEdit.pub.id;
      if (status) {
        payload.reference_criteria = value.id;
      } else {
        this.sourceSelected = {};
      }
      Vue.set(this.sourceVariant, value.id, status);
      this.$store.dispatch("comments/postRefGrade", payload).then(() => {
        this.loading = false;
        this.updateStetaComment();
      });
    }),

    /**
     * Get grade variant.
     * 
     * @param   {Bool}     status      The status (true or false)
     * @param   {Object}   value       The value
     * @param   {Object}   selected    The selected item
     * 
     * @return  {String}   the variant
     */
    getVariant(status, value, selected) {
      return status || value.id == selected.id ? "info" : "secondary";
    },

    /**
     * Called on post grade succeeded.
     */
    onPostSucceded() {
      this.$root.$emit("bv::toggle::collapse", "property-panel");
    },

    /**
     * Called on post grade failed.
     */
    onPostFailed() {
      this.$root.$emit("bv::toggle::collapse", "property-panel");
    }
  }
};
</script>
